import { useEffect, useState, useCallback } from "react";
import { Wallet, Clock, Hash, Plus, Pencil, Trash2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, StatCard, Loading, EmptyState } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = {
  employee_id: "", employee_name: "", amount: "", period: "",
  pay_date: new Date().toISOString().slice(0, 10), method: "Bank Transfer", status: "paid", notes: "",
};
const METHODS = ["Bank Transfer", "Cash", "Check", "Other"];

export default function Salaries() {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.get("/salary-payments").then((r) => setData(r.data)).catch((e) => setErr(apiErr(e))), []);
  useEffect(() => {
    load();
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, [load]);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...empty, ...row, amount: String(row.amount) }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) await api.put(`/salary-payments/${editing.id}`, payload);
      else await api.post("/salary-payments", payload);
      toast.success(editing ? "Payment updated" : "Payment recorded");
      setOpen(false); load();
    } catch (e2) { toast.error(apiErr(e2)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this salary payment record?")) return;
    await api.delete(`/salary-payments/${id}`); toast.success("Deleted"); load();
  };

  const onEmpSelect = (id) => {
    const emp = employees.find((x) => x.id === id);
    setForm((f) => ({ ...f, employee_id: id, employee_name: emp ? emp.name : f.employee_name }));
  };

  if (err) return (
    <div data-testid="salaries-page">
      <PageHeader title="Salaries" />
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto" />
        <p className="mt-4 text-muted-foreground">{err}</p>
      </div>
    </div>
  );
  if (!data) return <Loading />;
  const canManage = data.can_manage;

  return (
    <div data-testid="salaries-page">
      <PageHeader title="Salaries" subtitle="Record and track staff salary payments and their status."
        action={canManage && (
          <Button data-testid="add-salary-btn" onClick={openNew} className="bg-primary hover:bg-[#B35A42] text-primary-foreground">
            <Plus className="h-4 w-4 mr-1" /> Record payment
          </Button>
        )} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Wallet} label="Total Paid" value={money(data.total_paid)} hint="Marked as paid" />
        <StatCard icon={Clock} label="Pending" value={money(data.total_pending)} accent="text-secondary" hint="Awaiting payment" />
        <StatCard icon={Hash} label="Records" value={data.count} />
      </div>

      {data.by_employee?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="salary-month-summary">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground">This month · {data.month_label}</p>
              <p className="font-heading text-2xl font-semibold mt-1">{money(data.month_paid)} <span className="text-muted-foreground text-base font-normal">paid</span>
                {data.month_pending > 0 && <span className="text-base font-normal text-amber-600 ml-2">· {money(data.month_pending)} pending</span>}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.by_employee.map((e) => (
              <div key={e.employee_name} data-testid={`month-emp-${e.employee_name}`} className="rounded-lg border border-border p-3">
                <p className="font-medium truncate">{e.employee_name}</p>
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-emerald-600">Paid {money(e.paid)}</span>
                  {e.pending > 0 ? <span className="text-amber-600">Pending {money(e.pending)}</span> : <span className="text-muted-foreground">—</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Net this month: <span className="font-semibold text-foreground">{money(e.net)}</span></p>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.rows.length === 0 ? (
        <EmptyState icon={Wallet} title="No salary records yet"
          desc="Start recording your staff salary payments to keep a clear history of what was paid and when."
          action={canManage && <Button onClick={openNew} className="bg-primary text-primary-foreground"><Plus className="h-4 w-4 mr-1" /> Record payment</Button>} />
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Employee</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Pay Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`salary-row-${r.id}`}>
                  <TableCell className="font-medium">{r.employee_name}</TableCell>
                  <TableCell className="text-muted-foreground">{r.period || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.pay_date || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.method}</TableCell>
                  <TableCell className="text-right font-semibold">{money(r.amount)}</TableCell>
                  <TableCell>
                    <Badge className={`border-0 ${r.status === "paid" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}>
                      {r.status === "paid" ? "Paid" : "Pending"}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-salary-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(r.id)} data-testid={`delete-salary-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!canManage && (
        <p className="text-xs text-muted-foreground mt-3">You have view-only access to salaries. Ask the owner for permission to make changes.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit payment" : "Record salary payment"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>Employee</Label>
              {employees.length > 0 ? (
                <Select value={form.employee_id || ""} onValueChange={onEmpSelect}>
                  <SelectTrigger data-testid="salary-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.role}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input data-testid="salary-employee-name" required value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} placeholder="Employee name" />
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Amount</Label>
                <Input data-testid="salary-amount" type="number" step="0.01" min="0" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" /></div>
              <div className="space-y-1.5"><Label>Pay date</Label>
                <Input data-testid="salary-date" type="date" value={form.pay_date} onChange={(e) => setForm({ ...form, pay_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Period</Label>
                <Input data-testid="salary-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="e.g. June 2026" /></div>
              <div className="space-y-1.5"><Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="salary-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="salary-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes (bonus, deductions, etc.)" /></div>
            <DialogFooter>
              <Button type="submit" data-testid="salary-submit" className="bg-primary hover:bg-[#B35A42] text-primary-foreground">{editing ? "Save changes" : "Record payment"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
